/**
 * ReportSubHeader – sticky page header for report sub-pages (AI-BOM, Compliance, Print, etc.)
 *
 * @param title       - page title displayed next to logo
 * @param subtitle    - optional description line
 * @param actions     - right-side ReactNode (buttons)
 * @param currentPath - active report path for dropdown highlight (defaults to window.location.pathname)
 */

import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Package, Scale, Briefcase, Monitor } from 'lucide-react';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';

interface ReportSubHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /** Highlight the matching entry in the reports dropdown */
  currentPath?: string;
}

const REPORT_NAV = [
  {
    path: '/report',
    label: 'AI Risk Assessment',
    description: 'Main interactive report',
    icon: Monitor,
    disabled: false,
  },
  {
    path: '/report/business-summary',
    label: 'Business Summary',
    description: 'Coming soon',
    icon: Briefcase,
    disabled: true,
  },
  {
    path: '/report/ai-bom',
    label: 'AI Bill of Materials',
    description: 'Coming soon',
    icon: Package,
    disabled: true,
  },
  {
    path: '/report/compliance',
    label: 'Compliance Crosswalk',
    description: 'Coming soon',
    icon: Scale,
    disabled: true,
  },
] as const;

export function ReportSubHeader({ title, subtitle, actions, currentPath }: ReportSubHeaderProps) {
  const routerNavigate = useNavigate();
  const { search } = useLocation();
  const activePath = currentPath ?? window.location.pathname;

  const navigate = (path: string) => {
    routerNavigate(path + search);
  };

  return (
    <header className="no-print sticky top-0 z-50 border-b border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
        {/* Left – logo + title + reports nav dropdown */}
        <div className="flex items-center gap-3 min-w-0">
          <a href="/dashboard" className="shrink-0"><TrafficLightLogo size="sm" /></a>
          <div className="h-5 w-px bg-zinc-700 shrink-0" />

          {/* Reports navigation dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-zinc-800 transition-colors min-w-0"
                aria-label="Switch report type"
              >
                <div className="min-w-0 text-left">
                  <p className="text-sm font-semibold text-white truncate">{title}</p>
                  {subtitle && (
                    <p className="text-xs text-zinc-400 truncate hidden sm:block">{subtitle}</p>
                  )}
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              <DropdownMenuLabel className="text-xs text-zinc-500 uppercase tracking-wider">
                Switch Report
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {REPORT_NAV.map(({ path, label, description, icon: Icon, disabled }) => {
                const isActive = activePath === path;
                return (
                  <DropdownMenuItem
                    key={path}
                    onClick={() => !disabled && navigate(path)}
                    disabled={disabled}
                    className={cn(
                      'cursor-pointer',
                      isActive && 'bg-primary/10 text-primary',
                      disabled && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <Icon className="w-4 h-4 mr-2 shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className={cn('text-sm', isActive && 'font-medium')}>{label}</span>
                      <span className="text-xs text-muted-foreground truncate">{description}</span>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right – caller-supplied actions */}
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}
